import { CartItem, Product, ProductVariant } from '../types/marketplace';

interface MarketplaceCart {
  items: Array<{
    productId: string;
    variantId: string;
    quantity: number;
    product?: Product;
    variant?: ProductVariant;
  }>;
}

const CART_STORAGE_KEY = 'nupeloop_marketplace_cart';
const CART_EVENT_NAME = 'marketplace-cart-updated';

class MarketplaceCartService {
  getCart(): MarketplaceCart {
    if (typeof window === 'undefined') {
      return { items: [] };
    }

    try {
      const stored = window.localStorage.getItem(CART_STORAGE_KEY);
      if (!stored) {
        return { items: [] };
      }
      const parsed = JSON.parse(stored);
      if (!parsed || !Array.isArray(parsed.items)) {
        return { items: [] };
      }
      return parsed;
    } catch (_error) {
      return { items: [] };
    }
  }

  saveCart(cart: MarketplaceCart) {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    window.dispatchEvent(
      new CustomEvent(CART_EVENT_NAME, { detail: this.getItemCount(cart) }),
    );
  }

  addItem(product: Product, variant: ProductVariant, quantity: number) {
    const cart = this.getCart();
    const existing = cart.items.find(
      item => item.productId === product.id && item.variantId === variant.id,
    );

    if (existing) {
      existing.quantity += quantity;
      existing.product = product;
      existing.variant = variant;
    } else {
      cart.items.push({
        productId: product.id,
        variantId: variant.id,
        quantity,
        product,
        variant,
      });
    }

    this.saveCart(cart);
    return cart;
  }

  updateItemQuantity(productId: string, variantId: string, quantity: number) {
    const cart = this.getCart();
    const item = cart.items.find(
      entry => entry.productId === productId && entry.variantId === variantId,
    );

    if (!item) {
      return cart;
    }

    if (quantity <= 0) {
      return this.removeItem(productId, variantId);
    }

    item.quantity = quantity;
    this.saveCart(cart);
    return cart;
  }

  removeItem(productId: string, variantId: string) {
    const cart = this.getCart();
    cart.items = cart.items.filter(
      item => !(item.productId === productId && item.variantId === variantId),
    );
    this.saveCart(cart);
    return cart;
  }

  clearCart() {
    this.saveCart({ items: [] });
  }

  getSummary(cart: MarketplaceCart = this.getCart()) {
    let subtotal = 0;
    let totalItems = 0;
    let shippingTotal = 0;

    cart.items.forEach(item => {
      const price = item.variant?.price || 0;
      subtotal += price * item.quantity;
      totalItems += item.quantity;

      const shippingFee = item.product?.fulfillment?.shippingFee || 0;
      if (item.product?.fulfillment?.shippingAvailable && shippingFee > 0) {
        shippingTotal += shippingFee * item.quantity;
      }
    });

    return {
      subtotal,
      shippingTotal,
      total: subtotal + shippingTotal,
      totalItems,
    };
  }

  getItemCount(cart: MarketplaceCart = this.getCart()): number {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }
}

export const marketplaceCartService = new MarketplaceCartService();
export { CART_EVENT_NAME };
export type { MarketplaceCart };
